package com.patronat.top.client.presentation.main

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.patronat.top.client.deeplink.ClientDeepLinks
import com.patronat.top.client.platform.ClientBackHandler
import com.patronat.top.client.presentation.home.ClientHomeRoute
import com.patronat.top.client.presentation.map.ClientMapRoute
import com.patronat.top.client.presentation.profile.ClientProfileRoute
import com.patronat.top.client.presentation.saved.ClientSavedRoute
import com.patronat.top.ui.theme.AppIcons
import com.patronat.top.ui.theme.BodyLarge
import com.patronat.top.ui.theme.MineShaft400
import com.patronat.top.ui.theme.MineShaft950
import com.patronat.top.ui.theme.White
import org.jetbrains.compose.resources.stringResource
import top.clientapp.generated.resources.Res
import top.clientapp.generated.resources.client_tab_home
import top.clientapp.generated.resources.client_tab_map
import top.clientapp.generated.resources.client_tab_profile
import top.clientapp.generated.resources.client_tab_saved

@Composable
fun ClientMainScreen(
    webMode: Boolean = false,
    onAuthRequested: () -> Unit = {},
    modifier: Modifier = Modifier
) {
    var selectedTab by rememberSaveable { mutableStateOf(ClientMainTab.Home) }
    var tabBackStack by rememberSaveable { mutableStateOf(emptyList<ClientMainTab>()) }
    var isHomeFullScreen by rememberSaveable { mutableStateOf(false) }
    var isProfileFullScreen by rememberSaveable { mutableStateOf(false) }

    fun selectTab(tab: ClientMainTab) {
        if (tab == selectedTab) return
        tabBackStack = (tabBackStack.filterNot { it == tab } + selectedTab).takeLast(MaxTabBackStackSize)
        selectedTab = tab
    }

    fun navigateBackInTabs() {
        val previousTab = tabBackStack.lastOrNull()
        if (previousTab != null) {
            tabBackStack = tabBackStack.dropLast(1)
            selectedTab = previousTab
        } else if (selectedTab != ClientMainTab.Home) {
            selectedTab = ClientMainTab.Home
        }
    }

    LaunchedEffect(selectedTab) {
        if (selectedTab != ClientMainTab.Home) {
            isHomeFullScreen = false
        }
        if (selectedTab != ClientMainTab.Profile) {
            isProfileFullScreen = false
        }
    }

    ClientBackHandler(
        enabled = !webMode &&
            !isHomeFullScreen &&
            !isProfileFullScreen &&
            (tabBackStack.isNotEmpty() || selectedTab != ClientMainTab.Home),
        onBack = ::navigateBackInTabs
    )

    Column(
        modifier = modifier
            .fillMaxSize()
            .background(White)
            .windowInsetsPadding(WindowInsets.safeDrawing)
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f)
        ) {
            when (selectedTab) {
                ClientMainTab.Home -> ClientHomeRoute(
                    webMode = webMode,
                    onFullScreenChanged = { isHomeFullScreen = it }
                )
                ClientMainTab.Map -> ClientMapRoute(
                    onVenueDetailsRequested = { venueId ->
                        selectTab(ClientMainTab.Home)
                        ClientDeepLinks.open(ClientDeepLinks.venueUrl(venueId))
                    }
                )
                ClientMainTab.Saved -> ClientSavedRoute(
                    onVenueDetailsRequested = { venueId ->
                        selectTab(ClientMainTab.Home)
                        ClientDeepLinks.open(ClientDeepLinks.venueUrl(venueId))
                    }
                )
                ClientMainTab.Profile -> ClientProfileRoute(
                    onAuthRequested = onAuthRequested,
                    onFullScreenChanged = { isProfileFullScreen = it },
                    onVenueDetailsRequested = { venueId ->
                        selectTab(ClientMainTab.Home)
                        ClientDeepLinks.open(ClientDeepLinks.venueUrl(venueId))
                    }
                )
            }
        }

        if (!isHomeFullScreen && !isProfileFullScreen && !webMode) {
            ClientBottomNavigationBar(
                selectedTab = selectedTab,
                onTabSelected = ::selectTab
            )
        }
    }
}

@Composable
private fun ClientBottomNavigationBar(
    selectedTab: ClientMainTab,
    onTabSelected: (ClientMainTab) -> Unit,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .height(70.dp)
            .background(White)
            .padding(horizontal = 12.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        ClientMainTab.entries.forEach { tab ->
            ClientBottomNavigationItem(
                tab = tab,
                selected = tab == selectedTab,
                onClick = { onTabSelected(tab) },
                modifier = Modifier.weight(1f)
            )
        }
    }
}

@Composable
private fun ClientBottomNavigationItem(
    tab: ClientMainTab,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val label = tab.label()
    val contentColor = if (selected) ClientTabActiveColor else MineShaft400

    Column(
        modifier = modifier
            .height(60.dp)
            .clickable(onClick = onClick)
            .padding(top = 6.dp, bottom = 4.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        Icon(
            painter = tab.icon(selected),
            contentDescription = label,
            tint = Color.Unspecified,
            modifier = Modifier.size(24.dp)
        )

        Spacer(modifier = Modifier.height(4.dp))

        Text(
            text = label,
            color = contentColor,
            fontSize = 11.sp,
            lineHeight = 14.sp,
            fontWeight = FontWeight.Normal,
            textAlign = TextAlign.Center,
            maxLines = 1
        )
    }
}

@Composable
private fun ClientTabPlaceholder(
    title: String,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier.fillMaxSize(),
        contentAlignment = Alignment.Center
    ) {
        Text(
            text = title,
            style = BodyLarge.copy(
                color = MineShaft950,
                textAlign = TextAlign.Center
            )
        )
    }
}

private val ClientTabActiveColor = Color(0xFFC4A57B)
private const val MaxTabBackStackSize = 8

private enum class ClientMainTab {
    Home,
    Map,
    Saved,
    Profile
}

@Composable
private fun ClientMainTab.label(): String = when (this) {
    ClientMainTab.Home -> stringResource(Res.string.client_tab_home)
    ClientMainTab.Map -> stringResource(Res.string.client_tab_map)
    ClientMainTab.Saved -> stringResource(Res.string.client_tab_saved)
    ClientMainTab.Profile -> stringResource(Res.string.client_tab_profile)
}

@Composable
private fun ClientMainTab.icon(selected: Boolean): Painter = when (this) {
    ClientMainTab.Home -> if (selected) AppIcons.TabHomeFilled else AppIcons.TabHome
    ClientMainTab.Map -> if (selected) AppIcons.TabMapFilled else AppIcons.TabMap
    ClientMainTab.Saved -> if (selected) AppIcons.TabSavedFilled else AppIcons.TabSaved
    ClientMainTab.Profile -> if (selected) AppIcons.TabProfileFilled else AppIcons.TabProfile
}
